<template>
    <section>
        <div class="panel">
            <v-card outlined class="hero">

                <div class="mt-6 d-flex align-start">
                    <p class="signal-text" v-html="content.signal.text"></p>
                    <v-btn class="elevation-0" outlined @click="handleSignalAction">{{ content.signal.action }}</v-btn>
                </div>

                <v-btn small @click="toggleContentTitle" text class="arrow" :class="{ 'arrow--open': openContent }">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="#7b8394" stroke-width=".6" stroke-linecap="round" stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="m19 12-7 7-7-7" />
                    </svg>
                </v-btn>

                <transition name="body-drop">
                    <v-card v-if="openContent" outlined class="open-content">
                        <div class="flex-grow-1" style="width:100%">
                            <h1 v-html="content.title"></h1>
                            <p>{{ content.desc }}</p>
                        </div>
                    </v-card>
                </transition>

                <div class="stats">
                    <v-card outlined class="pa-5" v-for="(item, key) in content.grid" :key="key">
                        <small>{{ item.label }}</small>
                        <b :class="item.tone" v-html="item.value"></b>
                        <span :class="item.toneNote">{{ item.note }}</span>
                    </v-card>
                </div>
            </v-card>

            <!-- <v-card outlined class="signal">
                <b>{{ content.signal.label }}</b>
            </v-card> -->

            <v-card outlined v-if="content.section.type === 'ledger'" class="ledger">
                <div class="section-title">
                    <h2>{{ content.section.title }}</h2>
                    <span>{{ content.section.meta }}</span>
                    <button @click="$emit('open-new-operation')">{{ content.section.action }}</button>
                </div>
                <div class="filters">
                    <small>{{ uiContent.filterLabel }}</small>
                    <base-filter :groups="ledgerFilterGroups" :value="{ ledger: activeFilter }" :clearable="false"
                        @change="onLedgerFilterChange" />
                </div>
                <div class="table-scroll">
                    <base-data-table :headers="operationHeaders" :items="visibleOperations" :expanded="expandedRows"
                        item-key="id" :items-per-page="-1" hide-default-footer dense
                        class="operations-table elevation-0" @click:row="onRowClick">
                        <template v-slot:item.id="{ item }">
                            <span class="id text-caption text-no-wrap">{{ item.id }}</span>
                        </template>
                        <template v-slot:item.operation="{ item }">
                            <div class="operation-primary d-flex align-center">
                                <b>{{ item.name }}</b>
                                <i class="direction" :class="item.directionClass">{{ item.direction }}</i>
                            </div>


                            <small>{{ item.meta }}</small>
                        </template>
                        <template v-slot:item.risk="{ item }">
                            <i class="risk">{{ item.risk }}</i>
                        </template>
                        <template v-slot:item.notional="{ item }">
                            <div class="right">{{ item.notional }}<small>{{ item.converted }}</small></div>
                        </template>
                        <template v-slot:item.mtm="{ item }">
                            <div class="right" :class="mtmTone(item.mtm)">{{ item.mtm }}</div>
                        </template>
                        <template v-slot:item.coverage="{ item }">
                            <div class="coverage-list">
                                <div v-for="coverage in item.coverage" :key="coverage.label" class="coverage">
                                    <span>{{ coverage.label }} <b>{{ coverage.value }}%</b></span>
                                    <i><b :class="{ low: coverage.value < 50, warn: coverage.value >= 50 && coverage.value < 70 }"
                                            :style="{ width: coverage.value + '%' }"></b></i>
                                </div>
                            </div>
                        </template>
                        <template v-slot:item.settles="{ item }">
                            {{ item.settles }}<small>{{ item.days }}d</small>
                        </template>
                        <template v-slot:item.status="{ item }">
                            <i class="status" :class="item.statusClass">{{ item.status }}</i>
                        </template>
                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length" class="detail">
                                <div>
                                    <div><small>{{ uiContent.operationDetails.operation.title }}</small><b>{{ item.meta
                                    }}</b><span>{{ uiContent.operationDetails.operation.settlementPrefix }} {{
                                                item.settles }} · {{ item.days }} days</span></div>
                                    <div><small>{{ uiContent.operationDetails.risk.title }}</small><b>{{ item.risk
                                    }}</b><span>{{ uiContent.operationDetails.risk.description }}</span></div>
                                    <div><small>{{ uiContent.operationDetails.hedges.title }}</small><b
                                            :class="mtmTone(item.mtm)">{{ item.mtm }}</b><span>{{
                                                uiContent.operationDetails.hedges.description }}</span></div>
                                </div>
                            </td>
                        </template>
                    </base-data-table>
                </div>
            </v-card>

            <v-card outlined v-else-if="content.section.type === 'client-cards'" class="content-block">
                <div class="section-title">
                    <h2>{{ content.section.title }}</h2>
                    <span>{{ content.section.meta }}</span>
                </div>
                <div class="desk-grid">
                    <div v-for="card in content.section.cards" :key="card.title" class="client-card">
                        <span class="status" :class="statusDotClass(card.tone)" aria-hidden="true"></span>
                        <div class="name">{{ card.title }}</div>
                        <div class="sector">{{ card.eyebrow }}</div>
                        <div class="mini-grid">
                            <div v-for="metric in card.metrics" :key="metric.label">
                                <div class="lbl">{{ metric.label }}</div>
                                <div class="v" :class="metric.tone">{{ metric.value }} </div>
                            </div>
                        </div>
                    </div>
                </div>
            </v-card>

            <v-card outlined v-else class="content-block">
                <div class="section-title">
                    <h2>{{ content.section.title }}</h2>
                    <span>{{ content.section.meta }}</span>
                </div>
                <div class="content-grid">
                    <article v-for="card in content.section.cards" :key="card.title" class="content-card">
                        <div class="card-head">
                            <div><small>{{ card.eyebrow }}</small>
                                <h3>{{ card.title }}</h3>
                            </div>
                            <i :class="card.tone">{{ card.status }}</i>
                        </div>
                        <div class="metric-grid">
                            <div v-for="metric in card.metrics" :key="metric.label">
                                <small>{{ metric.label }}</small>
                                <b :class="metric.tone">{{ metric.value }}</b>
                            </div>
                        </div>
                    </article>
                </div>
            </v-card>

            <div class="grid" v-if="activePersona === 'cfo'">
                <hedge-book />
                <aggregate-hedge />
            </div>
            <div class="grid" v-else-if="activePersona === 'agri'">
                <port-basis />
                <payoff-structure />
            </div>
            <div class="grid" v-else-if="activePersona === 'desk'">
                <curve-dashboard />
                <heatmap />
            </div>
        </div>

        <hedge-ticket-drawer :value="ticketOpen" @input="!$event && $emit('close-drawer')"
            @primary="$emit('complete-action')" />
        <register-operation-drawer :value="newOperationOpen" @input="!$event && $emit('close-drawer')"
            @primary="$emit('complete-action')" />
        <div v-if="toast" class="toast">✓ {{ toast }}</div>
    </section>
</template>
<script>
import HedgeBook from "@/components/HedgeOS/components/cfo/HedgeBook.vue";
import AggregateHedge from "@/components/HedgeOS/components/cfo/AggregateHedge.vue";
import HedgeTicketDrawer from "@/components/HedgeOS/components/cfo/HedgeTicketDrawer.vue";
import RegisterOperationDrawer from "@/components/HedgeOS/components/cfo/RegisterOperationDrawer.vue";
import PortBasis from "@/components/HedgeOS/components/agri/PortBasis.vue";
import PayoffStructure from "@/components/HedgeOS/components/agri/PayoffStructure.vue";
import CurveDashboard from "@/components/HedgeOS/components/derivatives/CurveDashboard.vue";
import Heatmap from "@/components/HedgeOS/components/derivatives/Heatmap.vue";

export default {
    components: { HedgeBook, AggregateHedge, HedgeTicketDrawer, RegisterOperationDrawer, PortBasis, PayoffStructure, CurveDashboard, Heatmap },
    props: {
        visibleOperations: {
            type: Array,
            default: () => []
        },
        operationHeaders: {
            type: Array,
            default: () => []
        },
        activePersona: {
            type: String,
            required: true
        },
        content: {
            type: Object,
            required: true
        },
        uiContent: {
            type: Object,
            required: true
        },
        filters: {
            type: Array,
            default: () => []
        },
        activeFilter: {
            type: String,
            default: ''
        },
        expanded: {
            type: String,
            default: null
        },
        ticketOpen: Boolean,
        newOperationOpen: Boolean,
        toast: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            openContent: false
        }
    },
    methods: {
        toggleContentTitle() {
            this.openContent = !this.openContent;
        },
        handleSignalAction() {
            this.$emit('signal-action')
        },
        onRowClick(item) {
            this.$emit('toggle-operation', item.id)
        },
        mtmTone(value) {
            if (value === '—') return 'muted'
            return String(value).startsWith('-') ? 'negative' : 'positive'
        },
        onLedgerFilterChange({ value }) {
            this.$emit('filter-change', value)
        },
        statusDotClass(tone) {
            if (tone === 'negative') return 'red'
            if (tone === 'warning') return 'amber'
            return 'green'
        }
    },
    computed: {
        expandedRows() {
            if (!this.expanded) return []
            const row = this.visibleOperations.find(item => item.id === this.expanded)
            return row ? [row] : []
        },
        ledgerFilterGroups() {
            return [
                {
                    key: 'ledger',
                    multiple: false,
                    mandatory: true,
                    options: this.filters.map(filter => typeof filter === 'string'
                        ? { value: filter, label: filter }
                        : filter),
                },
            ]
        }
    }
}
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,600;0,900;1,400&family=JetBrains+Mono:wght@400;500&family=Inter+Tight:wght@400;500&display=swap');


.hedge-os:before {
    content: "";
    position: fixed;
    inset: 0;
    background: radial-gradient(ellipse at top left, rgba(255, 77, 31, .08), transparent 50%), radial-gradient(ellipse at bottom right, rgba(123, 196, 164, .05), transparent 50%);
    pointer-events: none
}

.arrow {
    position: absolute;
    z-index: 15;
    margin: 1rem;
}

.arrow svg {
    transition: transform 0.25s ease;
}

.arrow--open svg {
    transform: rotate(180deg);
}

.open-content {
    padding-top: 4rem;
    position: absolute;
    z-index: 10;
    width: 100%;
    height: 100%
}

button {
    font: inherit;
    color: inherit
}


.brand {
    font: 900 21px Fraunces;
    white-space: nowrap
}

.brand>i,
.session i {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--hot);
    margin-right: 9px;
    box-shadow: 0 0 16px var(--hot)
}

em {
    color: var(--main-color) !important;
}

.brand em {
    font-weight: 400
}

.brand small {
    font: 10px "JetBrains Mono";
    letter-spacing: .12em;
    color: var(--muted);
    margin-left: 12px
}

.ticker {
    display: flex;
    justify-content: center;
    gap: 20px;
    overflow: hidden;
    font: 11px "JetBrains Mono";
    white-space: nowrap
}

.ticker span {
    display: flex;
    gap: 7px
}

.ticker b {
    color: var(--muted);
    font-weight: 400
}

.up,
.positive {
    color: var(--cool) !important
}

.down,
.negative {
    color: var(--hot) !important
}

.session {
    font: 10px "JetBrains Mono";
    color: var(--muted);
    white-space: nowrap
}

.session i {
    width: 6px;
    height: 6px;
    background: var(--cool);
    box-shadow: 0 0 8px var(--cool)
}

.signal-text {
    font-size: 1.2rem !important;
    margin-right: 2rem;
}

.hero {
    display: grid;
    grid-template-columns: 1.25fr 1fr;
    gap: 48px;
    padding: 44px 30px 30px;
    border-bottom: 1px solid var(--line)
}

.hero h1 {
    font: 300 clamp(38px, 4vw, 58px)/.98 Fraunces;
    letter-spacing: -.035em
}

.hero h1::v-deep strong {
    display: block;
    font-weight: 900
}

.hero h1::v-deep em,
.signal-text ::v-deep em {
    color: var(--hot);
    font-weight: 400
}

.direction {
    border-radius: 999px;
}

.hero p,
.persona-placeholder p {
    color: var(--dim);
    font-size: 15px;
    line-height: 1.55;
    max-width: 650px;
}

.stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--line);
    border: 1px solid var(--line)
}

.stats article,
.persona-cards article {
    background: var(--ink);
    padding: 17px 20px
}

.stats small,
.persona-cards small {
    font: 9px "JetBrains Mono";
    letter-spacing: .17em;
    color: var(--muted)
}

.stats b,
.persona-cards b {
    display: block;
    font: 500 22px "JetBrains Mono";
    margin: 9px 0 7px
}

.stats sup {
    font-size: 13px;
    color: var(--muted)
}

.stats span,
.persona-cards span {
    display: block;
    font: 10px "JetBrains Mono";
    color: var(--muted)
}

.stats span.warning {
    color: #e8b64a;
    background: none !important;
}

.signal {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 28px;
    padding: 24px 30px;
    border-bottom: 1px solid var(--line)
}

.signal>b {
    border: 1px solid var(--hot);
    padding: 7px 10px;
    color: var(--hot);
    font: 9px "JetBrains Mono";
    letter-spacing: .17em
}

.signal p {
    font: 300 16px/1.45 Fraunces
}

.signal p::v-deep strong {
    font: 500 13px "JetBrains Mono"
}

.signal button,
.section-title button {
    background: transparent;
    border: 1px solid var(--paper);
    padding: 10px 16px;
    font-size: 11px;
    letter-spacing: .1em
}

.signal button:hover {
    background: var(--hot);
    border-color: var(--hot)
}

.ledger {
    padding: 28px 30px
}

.section-title {
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--line);
    padding-bottom: 13px
}

.section-title h2 {
    font: 600 15px Fraunces;
    letter-spacing: .12em
}

.section-title span {
    margin-left: auto;
    font: 9px "JetBrains Mono";
    letter-spacing: .14em;
    color: var(--muted)
}

.section-title button {
    margin-left: 16px;
    background: var(--hot);
    border-color: var(--hot);
    color: var(--ink);
    font-weight: 600
}

.panel {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    background: none !important;
    padding: 1.5rem;
    margin-top: 8rem;
}

.filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 20px 0
}

.filters small {
    font: 9px "JetBrains Mono";
    color: var(--muted);
    margin-right: 7px
}

.grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 1.5rem;
}

.content-block {
    padding: 28px 30px 42px;
}

.content-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    margin-top: 18px;
    background: #15171c;
    border: 1px solid var(--line);
}

.content-card {
    min-width: 0;
    padding: 22px;
    background: #15171c;
    transition: background .2s ease;
}

.content-card:hover {
    background: #1b1e24;
}

.desk-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: var(--line);
}

.client-card {
    background: var(--surface);
    padding: 1.2rem;
    position: relative;
    transition: background .2s;
    cursor: pointer;
}

.client-card:hover {
    background: #1b1e24;
}

.status {
    white-space: nowrap !important;

}

.operations-table::v-deep td:has(.status) {
    text-align: end !important;
    align-self: flex-end !important;
}

.client-card .status {
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 8px;
    min-width: 8px;
    height: 8px;
    min-height: 8px;
    display: block;
    padding: 0;
    line-height: 0;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    overflow: hidden;
}

.client-card .status.green {
    background: var(--cool);
}

.client-card .status.amber {
    background: var(--warn);
}

.client-card .status.red {
    background: var(--hot);
    animation: pulse 1.5s ease-in-out infinite;
}

.client-card .name {
    font-family: 'Fraunces', serif;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -.01em;
    margin-bottom: .3rem;
}

.client-card .sector {
    font-family: 'JetBrains Mono', monospace;
    font-size: .6rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: .15em;
    margin-bottom: 1rem;
}

.client-card .mini-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .8rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: .72rem;
}

.client-card .mini-grid .lbl {
    color: var(--muted);
    font-size: .58rem;
    text-transform: uppercase;
    letter-spacing: .12em;
}

.client-card .mini-grid .v {
    color: var(--paper);
    font-weight: 500;
    margin-top: .15rem;
}

.client-card .mini-grid .v.positive {
    color: var(--cool);
}

.client-card .mini-grid .v.negative {
    color: var(--hot);
}

.client-card .mini-grid .v.warning {
    background: transparent !important;
    background-color: transparent !important;
    color: var(--warn) !important;
}

@keyframes pulse {
    50% {
        opacity: .5
    }
}

.card-head {
    min-height: 76px;
    display: flex;
    justify-content: space-between;
    gap: 14px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--line);
}

.card-head small,
.metric-grid small {
    display: block;
    color: var(--muted);
    font: 9px "JetBrains Mono";
    letter-spacing: .12em;
    text-transform: uppercase;
}

.card-head h3 {
    margin-top: 7px;
    font: 600 17px Fraunces;
    letter-spacing: -.01em;
}

.card-head i {
    height: fit-content;
    padding: 4px 6px;
    border: 1px solid currentColor;
    font: normal 8px "JetBrains Mono";
    letter-spacing: .1em;
    white-space: nowrap;
}

.content-grid .warning {
    background: transparent !important;
    background-color: transparent !important;
    color: #e8b64a !important;
}

.metric-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px 14px;
    padding-top: 18px;
}

.metric-grid b {
    display: block;
    margin-top: 5px;
    color: var(--paper);
    font: 500 12px "JetBrains Mono";
}

.table-scroll {
    overflow-x: auto
}

.operations-table {
    color: var(--paper) !important;
}

.operations-table::v-deep table {
    width: 100%;
    border-collapse: collapse;
    font: 11px "JetBrains Mono"
}

.operations-table::v-deep th {
    padding: 10px;
    text-align: left;
    color: var(--muted);
    font-size: 9px;
    letter-spacing: .1em;
    font-weight: 400;
}

.operations-table::v-deep td {
    padding: 13px 10px;
    border-bottom: 1px solid var(--line);
    vertical-align: middle;
    height: 82px;
    box-sizing: border-box
}

.operations-table::v-deep tbody>tr {
    cursor: pointer;
}

.operations-table::v-deep tbody>tr:hover {
    background: #1b1e24
}

.operations-table::v-deep td b {
    font-family: Fraunces;
    font-size: 13px
}

.operations-table::v-deep td small {
    display: block;
    color: var(--muted);
    font-size: 9px;
    margin-top: 4px
}

.right {
    text-align: right
}

.id {
    color: var(--muted);
    letter-spacing: .08em;
    white-space: nowrap
}

.operation-primary {
    min-width: 190px;
    gap: 10px
}

.operation-primary b,
.operation-primary .direction,
.operations-table::v-deep td small,
.risk,
.status,
.right {
    white-space: nowrap
}

.direction,
.risk,
.status {
    display: inline-block;
    font-style: normal;
    font-size: 8px;
    letter-spacing: .1em;
    border: 1px solid;
    padding: 3px 5px;
    margin-right: 7px
}

.direction.import,
.status.open {
    color: white;
    border-color: var(--hot)
}

.direction.export,
.status.hedged {
    color: white;
    border-color: var(--cool)
}

.risk {
    color: var(--dim);
    border-color: var(--line)
}

.status.partial {
    color: white;
    border-color: #e8b64a
}

.coverage-list {
    min-width: 100px;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center
}

.coverage {
    min-width: 100px;
}

.coverage span {
    display: flex;
    justify-content: space-between;
    font-size: 8px;
    color: var(--muted)
}

.coverage>i {
    display: block;
    height: 4px;
    background: var(--ink3);
    margin: 3px 0 5px
}

.coverage>i b {
    display: block;
    height: 100%;
    background: var(--cool)
}

.coverage>i b.low {
    background: var(--hot)
}

.coverage>i b.warn {
    background: #e8b64a
}

.operations-table::v-deep td.detail {
    background: var(--ink3);
    padding: 20px;
    height: auto
}

.operations-table::v-deep td.detail>div {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--line)
}

.detail div div {
    background: #1b1e24;
    padding: 18px
}

.detail small,
.detail b,
.detail span {
    display: block
}

.detail b {
    margin: 10px 0
}

.detail span {
    color: var(--muted)
}

.persona-placeholder {
    padding: 70px 30px
}

.persona-placeholder h1 {
    font: 300 52px Fraunces;
    margin-top: 15px
}

.persona-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--line);
    margin-top: 55px
}

.persona-cards article {
    padding: 30px
}

.persona-cards b {
    font-size: 30px
}

.toast {
    position: fixed;
    right: 25px;
    bottom: 25px;
    background: #1b1e24;
    border: 1px solid var(--cool);
    border-left: 3px solid var(--cool);
    padding: 16px 20px;
    z-index: 30;
    font: 11px "JetBrains Mono"
}

/** animation */
.body-drop-enter-active {
    transition:
        max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.22s ease;
}

.body-drop-leave-active {
    transition:
        max-height 0.22s ease,
        opacity 0.15s ease;
}

.body-drop-enter,
.body-drop-leave-to {
    max-height: 0;
    opacity: 0;
}

.body-drop-enter-to,
.body-drop-leave {
    max-height: 100%;
    opacity: 1;
}

@media(max-width:1100px) {
    .ticker {
        display: none
    }

    .top {
        grid-template-columns: 1fr auto
    }

    .hero {
        grid-template-columns: 1fr
    }

    .grid {
        grid-template-columns: 1fr
    }

    .content-grid {
        grid-template-columns: 1fr 1fr;
    }

    .desk-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .signal {
        grid-template-columns: 1fr
    }

    .signal button {
        justify-self: start
    }

    .brand small {
        display: none
    }
}

@media(max-width:700px) {
    .top {
        padding: 0 16px
    }

    .session {
        display: none
    }

    .personas button {
        padding: 16px 12px
    }

    .personas span {
        display: none
    }

    .personas strong {
        font-size: 15px
    }

    .hero,
    .ledger {
        padding-left: 16px;
        padding-right: 16px
    }

    .stats {
        grid-template-columns: 1fr
    }

    .content-block {
        padding-left: 16px;
        padding-right: 16px;
    }

    .content-grid {
        grid-template-columns: 1fr;
    }

    .desk-grid {
        grid-template-columns: 1fr;
    }

    .section-title span {
        display: none
    }

    .detail td>div,
    .persona-cards {
        grid-template-columns: 1fr
    }
}
</style>
